/**
 * NOS-249: og:image resolution — renderer-agnostic (SPA today, Astro in M22).
 *
 * Every public page resolves its social share image through the same chain:
 *   explicit override → imported/cover image → generated branded default.
 * The generated default is served by the `/api/og` edge route (see
 * ogImageTemplate.ts) on the org's own public origin, so it works on both
 * subdomains and custom domains.
 */

/** Longest title the OG template renders before truncation (keeps URLs sane). */
export const OG_TITLE_MAX = 90

function truncateTitle(title: string): string {
  const t = title.trim().replace(/\s+/g, " ")
  if (t.length <= OG_TITLE_MAX) return t
  return `${t.slice(0, OG_TITLE_MAX - 1).trimEnd()}…`
}

/**
 * URL of the template-generated OG image for a page on an org's site.
 * `origin` is the public site origin (getPublicSiteOrigin); `orgSlug` is the
 * org's slug (host alone is ambiguous behind the shared Vercel deployment).
 */
export function buildDefaultOgImageUrl(
  origin: string,
  orgSlug: string,
  title: string
): string {
  const params = new URLSearchParams({ slug: orgSlug, title: truncateTitle(title) })
  return `${origin.replace(/\/+$/, "")}/api/og?${params.toString()}`
}

export interface OgImageSources {
  /** Explicit per-entity override (pages.meta.og_image, blog_posts/events.og_image). */
  explicit?: string | null
  /** Imported or authored cover image (blog_posts/events.cover_image). */
  cover?: string | null
  /** Generated branded default (buildDefaultOgImageUrl). */
  generatedDefault: string
}

/** Resolve og:image: explicit override → cover image → generated default. */
export function resolveOgImage(sources: OgImageSources): string {
  const explicit = sources.explicit?.trim()
  if (explicit) return explicit
  const cover = sources.cover?.trim()
  if (cover) return cover
  return sources.generatedDefault
}
